import React, { useState, useRef, useEffect } from "react";
import "./FileUpload.css";
import { apiClient, API_BASE_URL } from "../config/api";

const FileUpload = ({ onUploadComplete, onClose, initialFiles }) => {
  const [selectedFiles, setSelectedFiles] = useState(initialFiles || []);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileLabel, setCurrentFileLabel] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);
  const autoStarted = useRef(false);

  // If initialFiles provided — auto-start upload; otherwise auto-open file picker
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      if (!autoStarted.current) {
        autoStarted.current = true;
        handleUpload(initialFiles);
      }
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      setError("");
      setSuccess("");
      setUploadProgress(0);
      setCurrentFileLabel("");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleUpload = async (filesToUpload) => {
    const files = filesToUpload || selectedFiles;
    if (files.length === 0) {
      setError("Пожалуйста, выберите хотя бы один файл");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");
    setUploadProgress(0);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Требуется авторизация");
      }

      const successfulFiles = [];
      const failedFiles = [];

      // Каждый файл загружается и финализируется отдельно.
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        setCurrentFileLabel(`${fileIndex + 1}/${selectedFiles.length}: ${file.name}`);

        try {
          // Размер чанка: 2MB (безопасно для API Gateway)
          const CHUNK_SIZE = 2 * 1024 * 1024;
          const fileSize = file.size;
          const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

          // Шаг 1: Инициализируем загрузку
          const initResult = await apiClient.post("/user/upload-file-init", {
            filename: file.name,
            fileSize,
            totalChunks,
          });

          if (!initResult || !initResult.uploadId) {
            throw new Error(initResult?.error || "Не удалось инициализировать загрузку");
          }

          const { uploadId } = initResult;

          // Шаг 2: Загружаем чанки
          for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileSize);
            const chunk = file.slice(start, end);

            const formData = new FormData();
            formData.append("file", chunk, file.name);
            formData.append("uploadId", uploadId);
            formData.append("chunkIndex", chunkIndex);
            formData.append("totalChunks", totalChunks);

            // Для FormData используем fetch напрямую (apiClient не поддерживает FormData)
            const chunkResponse = await fetch(`${API_BASE_URL}/user/upload-file-chunk`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            });

            if (!chunkResponse.ok) {
              let errorData = {};
              try {
                errorData = await chunkResponse.json();
              } catch (_error) {
                errorData = {};
              }
              throw new Error(errorData.error || `Ошибка загрузки чанка ${chunkIndex + 1}`);
            }

            const fileProgress = (chunkIndex + 1) / totalChunks;
            const totalProgress = Math.round(
              ((fileIndex + fileProgress) / files.length) * 100
            );
            setUploadProgress(totalProgress);
          }

          // Шаг 3: Финализируем загрузку (собираем чанки)
          const result = await apiClient.post("/user/upload-file-finalize", {
            uploadId,
            filename: file.name,
          });

          if (!result || result.error) {
            throw new Error(result?.error || "Ошибка финализации загрузки");
          }

          successfulFiles.push(file.name);
          if (onUploadComplete) {
            onUploadComplete(result);
          }
        } catch (fileError) {
          failedFiles.push({
            name: file.name,
            message: fileError.message || "Ошибка загрузки файла",
          });
        }
      }

      if (successfulFiles.length > 0) {
        setSuccess(`Успешно загружено файлов: ${successfulFiles.length}`);
      }

      if (failedFiles.length > 0) {
        setError(
          `Не удалось загрузить: ${failedFiles
            .map((item) => `${item.name} (${item.message})`)
            .join(", ")}`
        );
      }

      setSelectedFiles([]);
      setCurrentFileLabel("");
      setUploadProgress(0);

      if (failedFiles.length === 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (uploadError) {
      console.error("Upload error:", uploadError);
      setError(uploadError.message || "Ошибка при загрузке файла");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setError("");
    setSuccess("");
    setUploadProgress(0);
    setCurrentFileLabel("");
  };

  const isAutoMode = !!(initialFiles && initialFiles.length > 0);

  return (
    <div className="file-upload-container">
      {!isAutoMode && (
        <div className="file-input-wrapper">
          <input
            type="file"
            id="file-input"
            ref={fileInputRef}
            accept=".crd,.xlsx,.xml"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            className="file-input"
          />
          <label htmlFor="file-input" className="file-input-label">
            {selectedFiles.length > 0 ? (
              <div className="selected-file-info">
                <span className="file-icon">📄</span>
                <div className="file-details">
                  <div className="file-name">Выбрано файлов: {selectedFiles.length}</div>
                  <div className="selected-files-list">
                    {selectedFiles.map((file) => (
                      <div key={`${file.name}-${file.size}`} className="selected-file-row">
                        <span>{file.name}</span>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="file-placeholder">
                <span className="upload-icon">☁️</span>
                <span>Выберите файл для загрузки (формат: .crd, .xlsx, .xml НД НДС)</span>
              </div>
            )}
          </label>
        </div>
      )}

      {isAutoMode && uploading && (
        <div style={{ fontSize: '13px', marginBottom: '6px', color: '#555' }}>
          {selectedFiles.map(f => f.name).join(', ')}
        </div>
      )}

      {uploading && (
        <div className="upload-progress">
          {currentFileLabel && <div className="progress-current-file">{currentFileLabel}</div>}
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-text">{uploadProgress}%</div>
        </div>
      )}

      {error && <div className="upload-message error">{error}</div>}
      {success && <div className="upload-message success">{success}</div>}

      {!isAutoMode && (
        <div className="upload-actions">
          {selectedFiles.length > 0 && !uploading && (
            <button className="btn-cancel" onClick={handleCancel}>
              Отменить
            </button>
          )}
          <button
            className="btn-upload"
            onClick={() => handleUpload()}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? "Загружается..." : "Загрузить"}
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
